import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { Settings, HelpCircle } from "lucide-react";
import ImageEditor from "../ImageEditor";
import GridConfigModal, { getOptimalGrid, type GridConfig } from "../GridConfigModal";
import AnimationConfigModal, { type AnimationConfig } from "../AnimationConfigModal";
import {
  createAnimations as createEncodedAnimations,
  disposeAnimationEncoders,
  type AnimationFormatKey,
} from "../../lib/animationEncoding";
import { dataURItoBlob, processImageBatch, type FormatProgress } from "../../lib/conversionPipeline";
import { downloadResultsForMime, saveAs } from "../../lib/downloads";
import {
  animationFormats,
  formats,
  formatFileSize,
  isPreviewable,
  transparencyMimes,
  type ImageFormat,
} from "../../lib/imageFormats";
import {
  createBaseSource,
  createFileList,
  createTransparentSource,
  getSupportedImageFiles,
  isHeicFile,
  prepareInputImage,
} from "../../lib/imageIngestion";
import type { FormatResults, ResultImage, SourceImage } from "../../lib/imageTypes";
import {
  revokeAnimationResults,
  revokeResults,
  revokeSources,
  revokeUrl,
} from "../../lib/objectUrls";
import { stitchImages } from "../../lib/stitching";
import {
  classifyTelemetryError,
  toCountBucket,
  trackTelemetryEvent,
} from "../../lib/telemetry";
import {
  DEFAULT_QUALITY_SETTINGS,
  QUALITY_FORMAT_BY_SETTING,
  normalizeQualitySettings,
  type QualitySettings,
} from "../../lib/qualitySettings";
import {
  disposeWorkers,
  getAdaptiveConversionConcurrency,
  removeBackgroundInWorker,
} from "../../lib/workerClients";

// Theme types
type Theme = "dark" | "light";
type FormatProgressState = FormatProgress & { canceled?: boolean };

const FETCH_TIMEOUT_MS = 30000;
const MAX_REMOTE_IMAGE_BYTES = 200 * 1024 * 1024;
const QUALITY_RERUN_DELAY_MS = 250;

const getProgressPercent = (progress?: FormatProgressState) => {
  if (!progress?.total) return 0;
  return Math.min(100, Math.round((progress.completed / progress.total) * 100));
};

const getFormatStatusMessage = (progress: FormatProgressState | undefined, error: string | undefined) => {
  if (progress?.canceled) return "Canceled";
  if (error) return error;
  if (progress && progress.failed > 0) {
    return `${progress.failed} failed`;
  }
  return null;
};

const getUserFacingErrorMessage = (message?: string) => {
  const type = classifyTelemetryError(message);
  if (type === "timeout") return "Timed out. Try fewer or smaller images.";
  if (type === "cors" || type === "fetch") return "Could not import that image URL. Try downloading the file first.";
  if (type === "unsupported") return "This file is not supported by the browser.";
  if (type === "background_removal") return "Background removal failed. Try a photo with a clearer subject.";
  if (type === "zip") return "ZIP creation failed. Try downloading fewer files at once.";
  if (type === "worker") return "The browser encoder stopped. Clear and retry with fewer files.";
  if (type === "ffmpeg") return "Animation encoding failed. Try fewer frames or a shorter animation.";
  if (type === "encode") return "This format could not be encoded. Try a smaller image.";
  return "Conversion failed. Try a smaller file or another browser.";
};

const getFormatTelemetryKey = (format: Pick<ImageFormat, "mime" | "ext">) =>
  format.ext || format.mime.replace(/[^a-z0-9]+/gi, "_").toLowerCase();

const getMimeTelemetryKey = (mime: string) => {
  const format = formats.find((item) => item.mime === mime);
  return format ? getFormatTelemetryKey(format) : mime.replace(/[^a-z0-9]+/gi, "_").toLowerCase();
};

const now = () => (typeof performance !== "undefined" ? performance.now() : Date.now());

// Get initial theme from localStorage or system preference
const getInitialTheme = (): Theme => {
  if (typeof window === "undefined") return "dark";
  const stored = localStorage.getItem("theme") as Theme | null;
  if (stored === "light" || stored === "dark") return stored;
  return window.matchMedia("(prefers-color-scheme: light)").matches ? "light" : "dark";
};

const App = () => {
  const jobIdRef = useRef(0);
  const formatAbortControllersRef = useRef<Map<string, AbortController>>(new Map());
  const qualityRerunTimeoutsRef = useRef<Map<string, number>>(new Map());
  const qualityRerunGenerationRef = useRef<Map<string, number>>(new Map());
  const qualitySettingsRef = useRef<QualitySettings>(DEFAULT_QUALITY_SETTINGS);

  const abortAllFormatConversions = useCallback(() => {
    formatAbortControllersRef.current.forEach((controller) => controller.abort());
    formatAbortControllersRef.current.clear();
  }, []);

  const bumpJobId = useCallback(() => {
    qualityRerunTimeoutsRef.current.forEach((timeoutId) => window.clearTimeout(timeoutId));
    qualityRerunTimeoutsRef.current.clear();
    qualityRerunGenerationRef.current.clear();
    abortAllFormatConversions();
    jobIdRef.current += 1;
    return jobIdRef.current;
  }, [abortAllFormatConversions]);

  const getFormatAbortKey = useCallback(
    (mime: string, jobId = jobIdRef.current) => `${jobId}:${mime}`,
    []
  );

  const getFormatAbortSignal = useCallback((mime: string, jobId?: number) => {
    const key = getFormatAbortKey(mime, jobId);
    let controller = formatAbortControllersRef.current.get(key);
    if (!controller || controller.signal.aborted) {
      controller = new AbortController();
      formatAbortControllersRef.current.set(key, controller);
    }
    return controller.signal;
  }, [getFormatAbortKey]);

  const abortFormatConversion = useCallback((mime: string, jobId?: number) => {
    const key = getFormatAbortKey(mime, jobId);
    formatAbortControllersRef.current.get(key)?.abort();
    formatAbortControllersRef.current.delete(key);
  }, [getFormatAbortKey]);

  const isJobActive = useCallback((jobId: number) => jobIdRef.current === jobId, []);
  const [sources, setSources] = useState<SourceImage[]>([]);
  const [sourceFiles, setSourceFiles] = useState<File[]>([]);
  const [results, setResults] = useState<FormatResults>({});
  const [processing, setProcessing] = useState<Set<string>>(new Set());
  const [formatProgress, setFormatProgress] = useState<Record<string, FormatProgressState>>({});
  const [formatErrors, setFormatErrors] = useState<Record<string, string>>({});
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [supportsAvif, setSupportsAvif] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [compress, setCompress] = useState(true);
  const [removeBackgroundEnabled, setRemoveBackgroundEnabled] = useState(false);
  const [showAdvancedSettings, setShowAdvancedSettings] = useState(false);
  const [qualitySettings, setQualitySettings] = useState<QualitySettings>(DEFAULT_QUALITY_SETTINGS);

  // Image editor state
  const [editingImageIndex, setEditingImageIndex] = useState<number | null>(null);

  // Animation states
  const [frameDelay, setFrameDelay] = useState(500);
  const [crossfade, setCrossfade] = useState(false);
  const [animationResults, setAnimationResults] = useState<Record<string, ResultImage>>({});
  const [processingAnimations, setProcessingAnimations] = useState<Set<string>>(new Set());
  const [animationErrors, setAnimationErrors] = useState<Record<string, string>>({});
  const [cancelledAnimationFormats, setCancelledAnimationFormats] = useState<Set<AnimationFormatKey>>(new Set());

  // Stitch/grid states
  const [stitchMode, setStitchMode] = useState(false);
  const [stitchType, setStitchType] = useState<"grid" | "animation">("grid");
  const [gridConfig, setGridConfig] = useState<GridConfig>({
    columns: 2,
    rows: 1,
    gap: 0,
    backgroundColor: "transparent",
    fit: "true-size",
  });
  const [showGridModal, setShowGridModal] = useState(false);
  const [showAnimationModal, setShowAnimationModal] = useState(false);
  const [showRemoveBgHelp, setShowRemoveBgHelp] = useState(false);
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
  const stitchModeRef = useRef(false);
  const stitchTypeRef = useRef<"grid" | "animation">("grid");
  const gridConfigRef = useRef<GridConfig>(gridConfig);

  // New feature states
  const [theme, setTheme] = useState<Theme>(getInitialTheme);
  const [toast, setToast] = useState<string | null>(null);
  const toastTimeoutRef = useRef<number | null>(null);
  const appLoadedTelemetryRef = useRef(false);
  const sourcesRef = useRef<SourceImage[]>([]);
  const sourceFilesRef = useRef<File[]>([]);
  const resultsRef = useRef<FormatResults>({});
  const animationResultsRef = useRef<Record<string, ResultImage>>({});
  const cancelledFormatsRef = useRef<Set<string>>(new Set());
  const cancelledAnimationFormatsRef = useRef<Set<AnimationFormatKey>>(new Set());

  // Show toast notification
  const showToast = useCallback((message: string) => {
    setToast(message);
    if (toastTimeoutRef.current) {
      window.clearTimeout(toastTimeoutRef.current);
    }
    toastTimeoutRef.current = window.setTimeout(() => setToast(null), 2000);
  }, []);

  const clearResults = useCallback(() => {
    cancelledFormatsRef.current.clear();
    setFormatProgress({});
    setFormatErrors({});
    setResults((prev) => {
      revokeResults(prev);
      return {};
    });
  }, []);

  const clearFormatResult = useCallback((mime: string) => {
    setFormatProgress((previous) => {
      const next = { ...previous };
      delete next[mime];
      return next;
    });
    setFormatErrors((previous) => {
      const next = { ...previous };
      delete next[mime];
      return next;
    });
    setProcessing((previous) => {
      const next = new Set(previous);
      next.delete(mime);
      return next;
    });
    setResults((previous) => {
      const existing = previous[mime];
      if (!existing) return previous;
      existing.forEach((result) => {
        revokeUrl(result.url);
        revokeUrl(result.previewUrl);
      });
      const next = { ...previous };
      delete next[mime];
      return next;
    });
  }, []);

  const clearAnimationResults = useCallback(() => {
    cancelledAnimationFormatsRef.current.clear();
    setCancelledAnimationFormats(new Set());
    setAnimationErrors({});
    setAnimationResults((prev) => {
      revokeAnimationResults(prev);
      return {};
    });
  }, []);

  useEffect(() => {
    sourcesRef.current = sources;
  }, [sources]);

  useEffect(() => {
    sourceFilesRef.current = sourceFiles;
  }, [sourceFiles]);

  useEffect(() => {
    resultsRef.current = results;
  }, [results]);

  useEffect(() => {
    animationResultsRef.current = animationResults;
  }, [animationResults]);

  useEffect(() => {
    stitchModeRef.current = stitchMode;
  }, [stitchMode]);

  useEffect(() => {
    stitchTypeRef.current = stitchType;
  }, [stitchType]);

  useEffect(() => {
    gridConfigRef.current = gridConfig;
  }, [gridConfig]);

  const setAnimationProcessing = useCallback(
    (key: AnimationFormatKey, isProcessing: boolean, jobId?: number) => {
      if (jobId !== undefined && !isJobActive(jobId)) return;
      setProcessingAnimations((prev) => {
        const next = new Set(prev);
        if (isProcessing) {
          next.add(key);
        } else {
          next.delete(key);
        }
        return next;
      });
    },
    [isJobActive]
  );

  const upsertAnimationResult = useCallback(
    (key: AnimationFormatKey, result: ResultImage, jobId?: number) => {
      if (jobId !== undefined && !isJobActive(jobId)) return;
      if (cancelledAnimationFormatsRef.current.has(key)) {
        revokeUrl(result.url);
        revokeUrl(result.previewUrl);
        return;
      }
      setAnimationResults((prev) => {
        const existing = prev[key];
        if (existing) {
          revokeUrl(existing.url);
          revokeUrl(existing.previewUrl);
        }
        return {
          ...prev,
          [key]: result,
        };
      });
    },
    [isJobActive]
  );

  const cancelFormat = useCallback((mime: string) => {
    const pendingTimeout = qualityRerunTimeoutsRef.current.get(mime);
    if (pendingTimeout !== undefined) {
      window.clearTimeout(pendingTimeout);
      qualityRerunTimeoutsRef.current.delete(mime);
    }
    qualityRerunGenerationRef.current.set(
      mime,
      (qualityRerunGenerationRef.current.get(mime) || 0) + 1
    );
    cancelledFormatsRef.current.add(mime);
    abortFormatConversion(mime);
    trackTelemetryEvent("cc_conversion_cancelled", {
      format: getMimeTelemetryKey(mime),
    });
    setProcessing((prev) => {
      const next = new Set(prev);
      next.delete(mime);
      return next;
    });
    setFormatProgress((prev) => ({
      ...prev,
      [mime]: {
        completed: prev[mime]?.completed ?? 0,
        total: prev[mime]?.total ?? 0,
        failed: prev[mime]?.failed ?? 0,
        canceled: true,
      },
    }));
    setFormatErrors((prev) => {
      const next = { ...prev };
      delete next[mime];
      return next;
    });
  }, [abortFormatConversion]);

  const cancelAnimationFormat = useCallback((key: AnimationFormatKey) => {
    cancelledAnimationFormatsRef.current.add(key);
    trackTelemetryEvent("cc_conversion_cancelled", {
      format: key,
      stitch_type: "animation",
    });
    setCancelledAnimationFormats((prev) => {
      const next = new Set(prev);
      next.add(key);
      return next;
    });
    setProcessingAnimations((prev) => {
      const next = new Set(prev);
      next.delete(key);
      return next;
    });
    setAnimationErrors((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
  }, []);

  // Toggle theme
  const toggleTheme = useCallback(() => {
    setTheme((prev) => {
      const next = prev === "dark" ? "light" : "dark";
      localStorage.setItem("theme", next);
      return next;
    });
  }, []);

  // Apply theme to body
  useEffect(() => {
    document.body.classList.remove("light", "dark");
    document.body.classList.add(theme);
  }, [theme]);

  useEffect(() => {
    const qualityRerunTimeouts = qualityRerunTimeoutsRef.current;
    return () => {
      if (toastTimeoutRef.current) {
        window.clearTimeout(toastTimeoutRef.current);
      }
      qualityRerunTimeouts.forEach((timeoutId) => window.clearTimeout(timeoutId));
      qualityRerunTimeouts.clear();
      abortAllFormatConversions();
      revokeSources(sourcesRef.current);
      revokeResults(resultsRef.current);
      revokeAnimationResults(animationResultsRef.current);
      disposeWorkers();
      disposeAnimationEncoders();
    };
  }, [abortAllFormatConversions]);

  useEffect(() => {
    setSupportsAvif(
      typeof document !== "undefined" &&
        document.documentElement.classList.contains("avif")
    );

    const checkMobile = () => setIsMobile(window.innerWidth <= 768);
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  const availableFormats = useMemo(
    () => formats.filter((f) => f.mime !== "image/avif" || supportsAvif),
    [supportsAvif]
  );

  useEffect(() => {
    if (appLoadedTelemetryRef.current) return;
    appLoadedTelemetryRef.current = true;
    trackTelemetryEvent("cc_app_loaded", {
      format_count: availableFormats.length,
    });
  }, [availableFormats.length]);

  const processImages = useCallback(
    async (
      newSources: SourceImage[],
      shouldCompress: boolean,
      targetFormats: ImageFormat[] = availableFormats,
      jobId?: number,
      settings: QualitySettings = qualitySettingsRef.current
    ) => {
      if (jobId !== undefined && !isJobActive(jobId)) return;
      const batchStartedAt = now();
      const sourceCountBucket = toCountBucket(newSources.length);
      let trackedFirstResult = false;
      trackTelemetryEvent("cc_conversion_batch_started", {
        compressed: shouldCompress,
        format_count: targetFormats.length,
        remove_background: removeBackgroundEnabled,
        source_count_bucket: sourceCountBucket,
        stitch_mode: stitchMode,
        stitch_type: stitchMode ? stitchType : "none",
      });
      targetFormats.forEach((format) => cancelledFormatsRef.current.delete(format.mime));
      setFormatProgress((prev) => {
        const next = { ...prev };
        targetFormats.forEach((format) => {
          next[format.mime] = {
            completed: 0,
            total: newSources.length,
            failed: 0,
          };
        });
        return next;
      });
      setFormatErrors((prev) => {
        const next = { ...prev };
        targetFormats.forEach((format) => delete next[format.mime]);
        return next;
      });
      setProcessing((prev) => {
        const next = new Set(prev);
        targetFormats.forEach((f) => next.add(f.mime));
        return next;
      });

      await processImageBatch({
        sources: newSources,
        formats: targetFormats,
        shouldCompress,
        qualitySettings: settings,
        concurrency: getAdaptiveConversionConcurrency(),
        jobId,
        isJobActive,
        isFormatActive: (format) => !cancelledFormatsRef.current.has(format.mime),
        getSignal: (format) => getFormatAbortSignal(format.mime, jobId),
        onResult: (format, result) => {
          if ((jobId === undefined || isJobActive(jobId)) && !cancelledFormatsRef.current.has(format.mime)) {
            setResults((prev) => ({
              ...prev,
              [format.mime]: [...(prev[format.mime] || []), result],
            }));
            if (!trackedFirstResult) {
              trackedFirstResult = true;
              trackTelemetryEvent("cc_time_to_first_result", {
                elapsed_ms: Math.round(now() - batchStartedAt),
                format: getFormatTelemetryKey(format),
                source_count_bucket: sourceCountBucket,
              });
            }
          } else {
            revokeUrl(result.url);
            revokeUrl(result.previewUrl);
          }
        },
        onFormatProgress: (format, progress) => {
          if ((jobId === undefined || isJobActive(jobId)) && !cancelledFormatsRef.current.has(format.mime)) {
            setFormatProgress((prev) => ({
              ...prev,
              [format.mime]: progress,
            }));
          }
        },
        onFormatError: (format, message) => {
          if ((jobId === undefined || isJobActive(jobId)) && !cancelledFormatsRef.current.has(format.mime)) {
            trackTelemetryEvent("cc_conversion_failed", {
              failure_type: classifyTelemetryError(message),
              format: getFormatTelemetryKey(format),
              source_count_bucket: sourceCountBucket,
            });
            setFormatErrors((prev) => ({
              ...prev,
              [format.mime]: getUserFacingErrorMessage(message),
            }));
          }
        },
        onFormatDone: (format) => {
          if (
            (jobId === undefined || isJobActive(jobId)) &&
            !cancelledFormatsRef.current.has(format.mime)
          ) {
            trackTelemetryEvent("cc_format_completed", {
              elapsed_ms: Math.round(now() - batchStartedAt),
              format: getFormatTelemetryKey(format),
              source_count_bucket: sourceCountBucket,
            });
          }
          setProcessing((prev) => {
            if (
              (jobId !== undefined && !isJobActive(jobId)) ||
              cancelledFormatsRef.current.has(format.mime)
            ) {
              return prev;
            }
            const next = new Set(prev);
            next.delete(format.mime);
            return next;
          });
        },
      });
    },
    [availableFormats, getFormatAbortSignal, isJobActive, removeBackgroundEnabled, stitchMode, stitchType]
  );

  const processStitchedImage = useCallback(
    (
      stitched: SourceImage,
      shouldCompress: boolean,
      targetFormats?: ImageFormat[],
      jobId?: number,
      settings?: QualitySettings
    ) => {
      const processing = processImages([stitched], shouldCompress, targetFormats, jobId, settings);
      revokeSources([stitched]);
      processing.catch((err) => {
        console.error("Failed to process stitched image:", err);
      });
    },
    [processImages]
  );

  const createAnimations = useCallback(
    async (imageSources: SourceImage[], delayMs: number, shouldCompress: boolean, jobId?: number, useCrossfade?: boolean) => {
      if (imageSources.length < 2) return;
      if (jobId !== undefined && !isJobActive(jobId)) return;
      clearAnimationResults();
      const batchStartedAt = now();
      const sourceCountBucket = toCountBucket(imageSources.length);
      let trackedFirstResult = false;
      trackTelemetryEvent("cc_conversion_batch_started", {
        compressed: shouldCompress,
        format_count: animationFormats.length,
        source_count_bucket: sourceCountBucket,
        stitch_mode: true,
        stitch_type: "animation",
      });
      setAnimationErrors({});
      cancelledAnimationFormatsRef.current.clear();
      setCancelledAnimationFormats(new Set());
      setProcessingAnimations(new Set(animationFormats.map((format) => format.key)));
      await createEncodedAnimations({
        sources: imageSources,
        frameDelay: delayMs,
        compress: shouldCompress,
        crossfade: useCrossfade,
        jobId,
        isJobActive,
        isFormatActive: (key) => !cancelledAnimationFormatsRef.current.has(key),
        onProcessing: setAnimationProcessing,
        onResult: (key, result, eventJobId) => {
          upsertAnimationResult(key, result, eventJobId);
          if (
            !trackedFirstResult &&
            (eventJobId === undefined || isJobActive(eventJobId)) &&
            !cancelledAnimationFormatsRef.current.has(key)
          ) {
            trackedFirstResult = true;
            trackTelemetryEvent("cc_time_to_first_result", {
              elapsed_ms: Math.round(now() - batchStartedAt),
              format: key,
              source_count_bucket: sourceCountBucket,
              stitch_type: "animation",
            });
          }
        },
        onError: (key, message, eventJobId) => {
          if (
            (eventJobId === undefined || isJobActive(eventJobId)) &&
            !cancelledAnimationFormatsRef.current.has(key)
          ) {
            trackTelemetryEvent("cc_conversion_failed", {
              failure_type: classifyTelemetryError(message),
              format: key,
              source_count_bucket: sourceCountBucket,
              stitch_type: "animation",
            });
            setAnimationErrors((prev) => ({
              ...prev,
              [key]: getUserFacingErrorMessage(message),
            }));
          }
        },
      });
    },
    [clearAnimationResults, isJobActive, setAnimationProcessing, upsertAnimationResult]
  );

  // Handle drag start for reordering
  const handleThumbDragStart = useCallback((e: React.DragEvent, index: number) => {
    e.stopPropagation();
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", index.toString());
  }, []);

  // Handle drag over for reordering
  const handleThumbDragOver = useCallback((e: React.DragEvent, index: number) => {
    e.preventDefault();
    e.stopPropagation();
    if (draggedIndex !== null && draggedIndex !== index) {
      setDragOverIndex(index);
    }
  }, [draggedIndex]);

  // Handle drop for reordering
  const handleThumbDrop = useCallback((e: React.DragEvent, dropIndex: number) => {
    e.preventDefault();
    e.stopPropagation();

    if (draggedIndex === null || draggedIndex === dropIndex) {
      setDraggedIndex(null);
      setDragOverIndex(null);
      return;
    }

    setSources((prev) => {
      const newSources = [...prev];
      const [draggedItem] = newSources.splice(draggedIndex, 1);
      newSources.splice(dropIndex, 0, draggedItem);
      return newSources;
    });

    setSourceFiles((prev) => {
      if (prev.length === 0) return prev;
      const newFiles = [...prev];
      const [draggedFile] = newFiles.splice(draggedIndex, 1);
      newFiles.splice(dropIndex, 0, draggedFile);
      return newFiles;
    });

    setDraggedIndex(null);
    setDragOverIndex(null);
  }, [draggedIndex]);

  // Handle drag end
  const handleThumbDragEnd = useCallback(() => {
    setDraggedIndex(null);
    setDragOverIndex(null);
  }, []);

  const handleFiles = useCallback(
    async (
      files: FileList,
      shouldCompress: boolean,
      shouldRemoveBackground: boolean,
      mode: "append" | "replace" = "append"
    ) => {
      const imageFiles = getSupportedImageFiles(files);
      if (imageFiles.length === 0) return;

      const jobId = mode === "replace" ? bumpJobId() : jobIdRef.current;
      if (mode === "replace") {
        disposeWorkers();
        setSources((prev) => {
          revokeSources(prev);
          return [];
        });
        clearResults();
        setProcessing(new Set());
        clearAnimationResults();
        setProcessingAnimations(new Set());
      }
      const alphaFormats = availableFormats.filter((f) => transparencyMimes.has(f.mime));
      const opaqueFormats = availableFormats.filter((f) => !transparencyMimes.has(f.mime));
      const shouldGenerateTransparent = shouldRemoveBackground && alphaFormats.length > 0;
      if (shouldGenerateTransparent) {
        setProcessing((prev) => {
          const next = new Set(prev);
          alphaFormats.forEach((f) => next.add(f.mime));
          return next;
        });
      }

      const baseSources: Array<SourceImage | null> = new Array(imageFiles.length).fill(null);
      const transparentSources: Array<SourceImage | null> = new Array(imageFiles.length).fill(null);
      const baseFiles: Array<File | null> = new Array(imageFiles.length).fill(null);
      let baseProcessed = 0;
      let transparentProcessed = 0;

      const finalizeBase = async () => {
        if (!isJobActive(jobId)) {
          const transient = baseSources.filter((s): s is SourceImage => Boolean(s));
          revokeSources(transient);
          return;
        }
        const finalizedSources = baseSources.filter((s): s is SourceImage => Boolean(s));
        const finalizedFiles = baseFiles.filter((f): f is File => Boolean(f));
        if (mode === "replace") {
          setSourceFiles(finalizedFiles);
        } else {
          setSourceFiles((prev) => [...prev, ...finalizedFiles]);
        }
        if (finalizedSources.length > 0) {
          let allSources: SourceImage[];
          if (mode === "replace") {
            setSources(finalizedSources);
            allSources = finalizedSources;
          } else {
            const prevSources = sourcesRef.current;
            allSources = [...prevSources, ...finalizedSources];
            setSources(allSources);
          }

          // If stitch mode is enabled, stitch all images and process the result
          if (stitchModeRef.current && allSources.length >= 2) {
            clearResults();
            setProcessing(new Set());

            if (stitchTypeRef.current === "grid") {
              // Update grid config for new image count
              const optimal = getOptimalGrid(allSources.length);
              const newConfig = { ...gridConfigRef.current, ...optimal };
              setGridConfig(newConfig);
              const stitched = await stitchImages(allSources, newConfig);
              if (stitched) {
                processStitchedImage(
                  stitched,
                  shouldCompress,
                  shouldGenerateTransparent ? opaqueFormats : availableFormats,
                  jobId
                );
              }
            } else {
              // Animation mode
              clearAnimationResults();
              setProcessingAnimations(new Set());
              createAnimations(allSources, frameDelay, shouldCompress, jobId, crossfade);
            }
          } else if (!stitchModeRef.current) {
            processImages(
              finalizedSources,
              shouldCompress,
              shouldGenerateTransparent ? opaqueFormats : availableFormats,
              jobId
            );
          }
        } else if (mode === "replace") {
          setSources([]);
        }
      };

      const finalizeTransparent = () => {
        if (!isJobActive(jobId)) {
          const transient = transparentSources.filter((s): s is SourceImage => Boolean(s));
          revokeSources(transient);
          return;
        }
        if (!shouldGenerateTransparent) return;
        const finalizedSources = transparentSources.map(
          (src, index) => src || baseSources[index]
        ).filter((s): s is SourceImage => Boolean(s));
        if (finalizedSources.length > 0) {
          const transientSources = transparentSources.filter((src): src is SourceImage => Boolean(src));
          processImages(finalizedSources, shouldCompress, alphaFormats, jobId)
            .finally(() => {
              revokeSources(transientSources);
            });
        }
      };

      const maybeFinalizeTransparent = () => {
        if (
          shouldGenerateTransparent &&
          transparentProcessed === imageFiles.length &&
          baseProcessed === imageFiles.length
        ) {
          finalizeTransparent();
        }
      };

      const maybeFinalizeBase = () => {
        if (baseProcessed === imageFiles.length) {
          finalizeBase();
          maybeFinalizeTransparent();
        }
      };

      const markFilePreparedFailed = (file: File, err: unknown) => {
        console.error(isHeicFile(file) ? "Failed to convert HEIC:" : "Failed to prepare image:", err);
        showToast(isHeicFile(file)
          ? "Could not read that HEIC file. Try exporting it as JPEG first."
          : "Could not read one image. Try a smaller or different file."
        );
        baseProcessed++;
        maybeFinalizeBase();
        if (shouldGenerateTransparent) {
          transparentProcessed++;
          maybeFinalizeTransparent();
        }
      };

      const processFile = async (file: File, index: number) => {
        let prepared;
        try {
          prepared = await prepareInputImage(file);
        } catch (err) {
          markFilePreparedFailed(file, err);
          return;
        }

        baseFiles[index] = prepared.baseFile;

        createBaseSource(prepared)
          .then((source) => {
            baseSources[index] = source;
          })
          .catch(() => {
            baseSources[index] = null;
          })
          .finally(() => {
            baseProcessed++;
            maybeFinalizeBase();
          });

        if (shouldGenerateTransparent) {
          createTransparentSource(prepared, removeBackgroundInWorker)
            .then((source) => {
              transparentSources[index] = source;
            })
            .catch((err) => {
              console.error("Failed to remove background:", err);
              transparentSources[index] = null;
            })
            .finally(() => {
              transparentProcessed++;
              maybeFinalizeTransparent();
            });
        }
      };

      for (let i = 0; i < imageFiles.length; i++) {
        processFile(imageFiles[i], i);
      }
    },
    [availableFormats, bumpJobId, clearAnimationResults, clearResults, createAnimations, crossfade, frameDelay, isJobActive, processImages, processStitchedImage, showToast]
  );

  const rerunQualityFormat = useCallback(async (
    format: ImageFormat,
    settings: QualitySettings,
    generation: number
  ) => {
    const isCurrentRerun = () =>
      qualityRerunGenerationRef.current.get(format.mime) === generation;
    if (!isCurrentRerun()) return;
    if (sourcesRef.current.length === 0) return;
    if (stitchModeRef.current && stitchTypeRef.current === "animation") return;

    const jobId = jobIdRef.current;
    clearFormatResult(format.mime);
    cancelledFormatsRef.current.delete(format.mime);

    if (stitchModeRef.current && sourcesRef.current.length >= 2) {
      const stitched = await stitchImages(sourcesRef.current, gridConfigRef.current);
      if (!stitched) return;
      if (!isCurrentRerun() || !isJobActive(jobId)) {
        revokeSources([stitched]);
        return;
      }
      processStitchedImage(stitched, compress, [format], jobId, settings);
      return;
    }

    let sourcesToProcess = sourcesRef.current;
    let transientSources: SourceImage[] = [];
    if (
      removeBackgroundEnabled &&
      transparencyMimes.has(format.mime) &&
      sourceFilesRef.current.length > 0
    ) {
      const preparedSources = await Promise.all(
        sourceFilesRef.current.map(async (file, index) => {
          try {
            const prepared = await prepareInputImage(file);
            const source = await createTransparentSource(prepared, removeBackgroundInWorker);
            return { source, transient: true };
          } catch (error) {
            console.error("Failed to refresh background removal:", error);
            return { source: sourcesRef.current[index], transient: false };
          }
        })
      );
      transientSources = preparedSources
        .filter((entry) => entry.transient && entry.source)
        .map((entry) => entry.source);
      sourcesToProcess = preparedSources
        .map((entry) => entry.source)
        .filter((source): source is SourceImage => Boolean(source));
    }

    if (!isCurrentRerun() || !isJobActive(jobId)) {
      revokeSources(transientSources);
      return;
    }

    try {
      await processImages(sourcesToProcess, compress, [format], jobId, settings);
    } finally {
      revokeSources(transientSources);
    }
  }, [
    clearFormatResult,
    compress,
    isJobActive,
    processImages,
    processStitchedImage,
    removeBackgroundEnabled,
  ]);

  const scheduleQualityRerun = useCallback((
    setting: keyof QualitySettings,
    settings: QualitySettings
  ) => {
    const mime = QUALITY_FORMAT_BY_SETTING[setting];
    const format = availableFormats.find((candidate) => candidate.mime === mime);
    if (!format) return;

    abortFormatConversion(mime);
    setProcessing((previous) => {
      const next = new Set(previous);
      next.delete(mime);
      return next;
    });

    const previousTimeout = qualityRerunTimeoutsRef.current.get(mime);
    if (previousTimeout !== undefined) {
      window.clearTimeout(previousTimeout);
    }
    const generation = (qualityRerunGenerationRef.current.get(mime) || 0) + 1;
    qualityRerunGenerationRef.current.set(mime, generation);
    const timeoutId = window.setTimeout(() => {
      qualityRerunTimeoutsRef.current.delete(mime);
      rerunQualityFormat(format, settings, generation).catch((error) => {
        console.error("Failed to reprocess quality settings:", error);
        showToast("Could not reprocess with those quality settings.");
      });
    }, QUALITY_RERUN_DELAY_MS);
    qualityRerunTimeoutsRef.current.set(mime, timeoutId);
  }, [abortFormatConversion, availableFormats, rerunQualityFormat, showToast]);

  // Handle a single blob (from paste or URL import)
  const handleBlob = useCallback(
    (blob: Blob, name: string) => {
      const file = new File([blob], name, { type: blob.type });
      handleFiles(createFileList([file]), compress, removeBackgroundEnabled);
    },
    [handleFiles, compress, removeBackgroundEnabled]
  );

  const importImageUrl = useCallback(
    async (url: string) => {
      const trimmed = url.trim();
      if (!trimmed) return;
      try {
        const controller = new AbortController();
        const timeoutId = window.setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS);
        let response: Response;
        try {
          response = await fetch(trimmed, { mode: "cors", signal: controller.signal });
        } finally {
          window.clearTimeout(timeoutId);
        }
        if (!response.ok) {
          throw new Error(`HTTP error: ${response.status}`);
        }
        const contentType = response.headers.get("content-type") || "";
        if (!contentType.startsWith("image/")) {
          throw new Error("URL does not point to an image");
        }
        const contentLength = response.headers.get("content-length");
        if (contentLength && Number(contentLength) > MAX_REMOTE_IMAGE_BYTES) {
          throw new Error("Image is too large to import");
        }
        const blob = await response.blob();
        if (blob.size > MAX_REMOTE_IMAGE_BYTES) {
          throw new Error("Image is too large to import");
        }
        const ext = contentType.split("/")[1]?.split(";")[0] || "png";
        const filename = trimmed.split("/").pop()?.split("?")[0] || `image.${ext}`;
        handleBlob(blob, filename);
        showToast("Image imported!");
      } catch (err) {
        if (err instanceof DOMException && err.name === "AbortError") {
          showToast("Image fetch timed out.");
          return;
        }
        if (err instanceof TypeError && err.message.includes("Failed to fetch")) {
          showToast("CORS error: URL cannot be fetched directly.");
        } else if (err instanceof Error) {
          showToast(err.message);
        } else {
          showToast("Failed to fetch image");
        }
      }
    },
    [handleBlob, showToast]
  );

  // Clipboard paste handler
  useEffect(() => {
    const handlePaste = (e: ClipboardEvent) => {
      const items = e.clipboardData?.items;
      if (!items) return;

      for (let i = 0; i < items.length; i++) {
        const item = items[i];
        if (item.type.startsWith("image/")) {
          const blob = item.getAsFile();
          if (blob) {
            e.preventDefault();
            const ext = item.type.split("/")[1] || "png";
            handleBlob(blob, `pasted-image.${ext}`);
            showToast("Image pasted!");
            return;
          }
        }
      }

      const text = e.clipboardData?.getData("text");
      if (text) {
        const match = text.match(/https?:\/\/[^\s]+/i);
        if (match?.[0]) {
          const url = match[0].replace(/[),.;]+$/g, "");
          e.preventDefault();
          importImageUrl(url);
        }
      }
    };

    document.addEventListener("paste", handlePaste);
    return () => document.removeEventListener("paste", handlePaste);
  }, [handleBlob, showToast, importImageUrl]);

  const handleDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(e.type === "dragenter" || e.type === "dragover");
  }, []);

  const handlePageDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    }
  }, []);

  const handlePageDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    const x = e.clientX;
    const y = e.clientY;
    if (x <= rect.left || x >= rect.right || y <= rect.top || y >= rect.bottom) {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setDragActive(false);
      if (e.dataTransfer.files?.length) {
        handleFiles(e.dataTransfer.files, compress, removeBackgroundEnabled);
      }
    },
    [handleFiles, compress, removeBackgroundEnabled]
  );

  const handleClear = () => {
    bumpJobId();
    disposeWorkers();
    setSources((prev) => {
      revokeSources(prev);
      return [];
    });
    setSourceFiles([]);
    clearResults();
    setProcessing(new Set());
    clearAnimationResults();
    setProcessingAnimations(new Set());
    disposeAnimationEncoders();
  };

  const downloadAll = async (mime: string) => {
    const resultCount = results[mime]?.length || 0;
    const didDownload = await downloadResultsForMime({
      mime,
      results,
      onError: (err) => {
        console.error("Failed to create zip:", err);
        trackTelemetryEvent("cc_download_failed", {
          failure_type: classifyTelemetryError(err instanceof Error ? err.message : "zip failed"),
          format: getMimeTelemetryKey(mime),
          result_count_bucket: toCountBucket(resultCount),
        });
        showToast("Failed to create ZIP");
      },
    });
    if (didDownload) {
      trackTelemetryEvent("cc_download_started", {
        format: getMimeTelemetryKey(mime),
        result_count_bucket: toCountBucket(resultCount),
        zipped: resultCount > 1,
      });
    }
  };

  const handleFormatCardKeyDown = (
    event: React.KeyboardEvent<HTMLDivElement>,
    canDownload: boolean,
    download: () => void
  ) => {
    if (!canDownload || (event.key !== "Enter" && event.key !== " ")) return;
    event.preventDefault();
    download();
  };

  const updateQualitySetting = (key: keyof QualitySettings, value: number) => {
    const next = normalizeQualitySettings({
      ...qualitySettingsRef.current,
      [key]: value,
    });
    qualitySettingsRef.current = next;
    setQualitySettings(next);
    scheduleQualityRerun(key, next);
  };

  const totalSize = (mime: string): number => {
    return (results[mime] || []).reduce((sum, r) => sum + r.size, 0);
  };

  const totalOriginalSize = (mime: string): number => {
    return (results[mime] || []).reduce((sum, r) => sum + r.originalSize, 0);
  };

  return (
    <div
      className={`page-drop-target ${dragActive ? "dragging" : ""}`}
      onDragEnter={handlePageDrag}
      onDragOver={handlePageDrag}
      onDragLeave={handlePageDragLeave}
      onDrop={handleDrop}
    >
      {dragActive && (
        <svg className="marching-ants" viewBox="0 0 100 100" preserveAspectRatio="none" xmlns="http://www.w3.org/2000/svg">
          <rect x="0" y="0" width="100" height="100" rx="3" ry="3" vectorEffect="non-scaling-stroke" />
        </svg>
      )}

      {/* Theme toggle button */}
      <button
        className="theme-toggle"
        onClick={toggleTheme}
        aria-label={`Switch to ${theme === "dark" ? "light" : "dark"} mode`}
      >
        {theme === "dark" ? (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="5" />
            <line x1="12" y1="1" x2="12" y2="3" />
            <line x1="12" y1="21" x2="12" y2="23" />
            <line x1="4.22" y1="4.22" x2="5.64" y2="5.64" />
            <line x1="18.36" y1="18.36" x2="19.78" y2="19.78" />
            <line x1="1" y1="12" x2="3" y2="12" />
            <line x1="21" y1="12" x2="23" y2="12" />
            <line x1="4.22" y1="19.78" x2="5.64" y2="18.36" />
            <line x1="18.36" y1="5.64" x2="19.78" y2="4.22" />
          </svg>
        ) : (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
          </svg>
        )}
      </button>

      {/* Toast notification */}
      {toast && (
        <div className="toast">{toast}</div>
      )}

      <main>
        <h1 className="title">
          <span className="title-gradient">
          <svg className="camel-logo" viewBox="0 0 544 512" xmlns="http://www.w3.org/2000/svg">
            <path fill="currentColor" d="M181.198303,131.000000 C181.648956,142.464615 180.077820,153.515381 182.250244,164.397797 C185.877426,182.567612 200.615662,196.967331 218.867355,200.456329 C225.444885,201.713699 226.410629,200.934418 226.693573,194.250626 C227.685242,170.825821 232.225418,148.324356 244.438431,127.927399 C247.600983,122.645592 251.266006,117.700195 256.267120,113.855736 C268.945190,104.109886 283.619080,104.869873 295.099091,116.100304 C303.119385,123.946236 308.403992,133.509384 312.264496,143.941742 C316.790527,156.172623 319.473389,168.842468 321.390869,181.763672 C323.500916,168.542618 326.270142,155.515533 331.048737,143.013626 C334.951874,132.802063 340.177216,123.346535 348.278931,115.804047 C361.787262,103.228119 379.872314,104.498070 391.843475,118.590622 C403.894928,132.777649 409.222382,149.852615 413.177338,167.530273 C415.333221,177.166504 415.573303,187.022705 416.111023,196.828323 C416.287140,200.039581 417.276855,201.455933 420.785767,201.740463 C444.199951,203.639114 463.110229,223.390060 463.216370,247.019348 C463.495667,309.177124 463.297180,371.336975 463.384064,433.495850 C463.389282,437.225006 462.044189,438.425201 458.420105,438.381104 C446.256714,438.233124 434.090424,438.331818 421.925262,438.319458 C414.916229,438.312347 414.903809,438.297943 414.902954,431.462830 C414.897827,389.134674 414.896454,346.806549 414.899689,304.478394 C414.900238,297.049133 414.912933,297.033844 407.396271,297.029663 C366.068024,297.006653 324.739746,296.986755 283.411499,296.978363 C275.269379,296.976715 275.264771,297.002197 275.263245,304.906158 C275.255096,347.067657 275.255554,389.229126 275.236755,431.390625 C275.233673,438.314514 275.196198,438.321228 268.317535,438.323578 C256.652313,438.327545 244.987045,438.333801 233.321823,438.316071 C226.790771,438.306152 226.791794,438.285248 226.790466,431.547058 C226.782120,388.885651 226.779526,346.224213 226.757874,303.562805 C226.754623,297.165985 226.253998,296.875275 220.126236,296.328552 C184.730301,293.170471 159.033890,275.486816 142.590057,244.158875 C135.470261,230.594559 132.546082,216.068893 132.624710,200.780441 C132.772110,172.118027 132.688248,143.454330 132.670288,114.791206 C132.666290,108.393028 132.489243,108.240608 126.269028,108.225845 C114.770493,108.198555 103.267090,108.015427 91.775520,108.297150 C86.875671,108.417274 85.477898,106.612350 85.578423,101.910065 C85.838364,89.750465 85.810356,77.578857 85.583839,65.417885 C85.504921,61.181416 86.642838,59.563244 91.167969,59.591763 C119.330063,59.769264 147.494293,59.761219 175.656570,59.599461 C180.189926,59.573429 181.298676,61.319550 181.269409,65.512688 C181.118286,87.174232 181.198257,108.837402 181.198303,131.000000z"/>
          </svg>
          convertcamel
        </span>
      </h1>
      <p className="tagline" style={{ marginTop: '-20px' }}>A one stop shop to convert + compress all your images instantly.</p>

      <input
        type="file"
        id="file-input"
        className="hidden-input"
        accept="image/*,.heic,.heif,.svg"
        multiple
        ref={fileInputRef}
        onChange={(e) => e.target.files && handleFiles(e.target.files, compress, removeBackgroundEnabled)}
      />

      <div className="drop-zone-wrapper">
        {sources.length > 0 && (
          <button className="clear-btn" onClick={handleClear}>
            clear
          </button>
        )}
        <div
          role="button"
          tabIndex={0}
          aria-label="Upload images"
          className={`drop-zone ${dragActive ? "active" : ""}`}
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              fileInputRef.current?.click();
            }
          }}
        >
          {sources.length === 0 ? (
            <div className="drop-content">
              <span className="drop-text">Drop images here</span>
              <span className="drop-hint">
                PNG, JPG, WebP, AVIF, GIF, BMP, HEIC, SVG
              </span>
              <span className="drop-hint paste-hint">
                Or paste an image or an image URL anywhere
              </span>
            </div>
          ) : (
            <div className="source-preview">
              <div className="source-images">
                {sources.slice(0, 6).map((src, i) => (
                  <div
                    key={i}
                    className={`source-thumb ${draggedIndex === i ? "dragging" : ""} ${dragOverIndex === i ? "drag-over" : ""}`}
                    draggable
                    onDragStart={(e) => handleThumbDragStart(e, i)}
                    onDragOver={(e) => handleThumbDragOver(e, i)}
                    onDrop={(e) => handleThumbDrop(e, i)}
                    onDragEnd={handleThumbDragEnd}
                    onDragLeave={() => setDragOverIndex(null)}
                  >
                    <img src={src.url} alt="" draggable={false} />
                    <button
                      type="button"
                      className="edit-btn"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setEditingImageIndex(i);
                      }}
                      title="Edit image"
                    >
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M12 20h9" />
                        <path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5z" />
                      </svg>
                    </button>
                  </div>
                ))}
                {sources.length > 6 && (
                  <div className="source-thumb more">+{sources.length - 6}</div>
                )}
              </div>
              <span className="source-count">
                {sources.length} image{sources.length !== 1 ? "s" : ""} — drag to reorder, drop or click to add more
              </span>
            </div>
          )}
        </div>

      </div>

      {editingImageIndex !== null && sources[editingImageIndex] && (
        <ImageEditor
          imageUrl={sources[editingImageIndex].url}
          imageName={sources[editingImageIndex].name}
          onCancel={() => setEditingImageIndex(null)}
          onSave={async (editedDataUrl) => {
            const editedBlob = dataURItoBlob(editedDataUrl);
            const editedType = editedBlob.type || "image/png";
            const editedUrl = URL.createObjectURL(editedBlob);
            setSources((prev) => {
              const next = [...prev];
              const index = editingImageIndex;
              if (index === null || !next[index]) {
                URL.revokeObjectURL(editedUrl);
                return prev;
              }
              revokeUrl(next[index].url);
              next[index] = { ...next[index], blob: editedBlob, url: editedUrl };
              return next;
            });

            const updatedFiles = sourceFiles.length > 0 ? [...sourceFiles] : [];
            if (editingImageIndex !== null && updatedFiles.length > 0) {
              updatedFiles[editingImageIndex] = new File([editedBlob], sources[editingImageIndex].name, { type: editedType });
            }
            setSourceFiles((prevFiles) => {
              if (editingImageIndex === null) return prevFiles;
              const updated = [...prevFiles];
              updated[editingImageIndex] = new File([editedBlob], sources[editingImageIndex].name, { type: editedType });
              return updated;
            });
            clearResults();
            setProcessing(new Set());
            clearAnimationResults();
            setProcessingAnimations(new Set());
            const jobId = bumpJobId();
            const nextSources = sources.map((src, idx) => idx === editingImageIndex ? { ...src, blob: editedBlob, url: editedUrl } : src);
            if (stitchMode && stitchType === "animation" && sources.length >= 2) {
              createAnimations(nextSources, frameDelay, compress, jobId, crossfade);
            } else if (stitchMode && stitchType === "grid" && sources.length >= 2) {
              const stitched = await stitchImages(nextSources, gridConfig);
              if (stitched) {
                processStitchedImage(stitched, compress, undefined, jobId);
              }
            } else {
              if (removeBackgroundEnabled && updatedFiles.length > 0) {
                handleFiles(createFileList(updatedFiles), compress, removeBackgroundEnabled, "replace");
              } else {
                processImages(nextSources, compress, undefined, jobId);
              }
            }
            setEditingImageIndex(null);
          }}
        />
      )}

      {showGridModal && (
        <GridConfigModal
          imageCount={sources.length}
          images={sources}
          config={gridConfig}
          onCancel={() => setShowGridModal(false)}
          onSave={async (newConfig) => {
            const jobId = bumpJobId();
            setGridConfig(newConfig);
            setShowGridModal(false);
            // Re-stitch with new config
            clearResults();
            setProcessing(new Set());
            const stitched = await stitchImages(sources, newConfig);
            if (stitched) {
              processStitchedImage(stitched, compress, undefined, jobId);
            }
          }}
        />
      )}

      {showAnimationModal && (
        <AnimationConfigModal
          images={sources}
          frameDelay={frameDelay}
          crossfade={crossfade}
          onCancel={() => setShowAnimationModal(false)}
          onSave={(config: AnimationConfig) => {
            setFrameDelay(config.frameDelay);
            setCrossfade(config.crossfade);
            setShowAnimationModal(false);
            // Re-create animations with new settings
            if (sources.length >= 2) {
              clearAnimationResults();
              setProcessingAnimations(new Set());
              const jobId = bumpJobId();
              createAnimations(sources, config.frameDelay, compress, jobId, config.crossfade);
            }
          }}
        />
      )}

      {showRemoveBgHelp && (
        <div className="grid-config-overlay" onClick={() => setShowRemoveBgHelp(false)}>
          <div className="grid-config-modal remove-bg-help-modal" onClick={(e) => e.stopPropagation()}>
            <div className="grid-config-header">
              <h3>About Background Removal</h3>
              <button className="grid-config-close-btn" onClick={() => setShowRemoveBgHelp(false)}>
                &times;
              </button>
            </div>
            <div className="grid-config-content">
              <p className="remove-bg-help-text">
                Background removal works best with photos of <strong>people</strong>. Results may be inconsistent or less accurate with other subjects like objects, animals, or complex scenes.
              </p>
              <p className="remove-bg-help-text">
                For best results, use images with clear subject-background separation and good lighting.
              </p>
            </div>
            <div className="grid-config-actions">
              <button className="grid-config-save-btn" onClick={() => setShowRemoveBgHelp(false)}>
                Got it
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="options-row">
        <label className="compress-toggle">
          <input
            type="checkbox"
            checked={compress}
            onChange={async (e) => {
              const newCompress = e.target.checked;
              setCompress(newCompress);
              if (sources.length > 0 && !(stitchMode && stitchType === "animation")) {
                const jobId = bumpJobId();
                clearResults();
                setProcessing(new Set());
                if (stitchMode && stitchType === "grid" && sources.length >= 2) {
                  const stitched = await stitchImages(sources, gridConfig);
                  if (stitched) {
                    processStitchedImage(stitched, newCompress, undefined, jobId);
                  }
                } else if (removeBackgroundEnabled && sourceFiles.length > 0) {
                  clearAnimationResults();
                  setProcessingAnimations(new Set());
                  handleFiles(createFileList(sourceFiles), newCompress, removeBackgroundEnabled, "replace");
                } else {
                  processImages(sources, newCompress, undefined, jobId);
                }
              } else if (sources.length >= 2 && stitchMode && stitchType === "animation") {
                const jobId = bumpJobId();
                createAnimations(sources, frameDelay, newCompress, jobId, crossfade);
              }
            }}
          />
          <span className="checkmark" />
          <span className="compress-label">Compress images</span>
        </label>

        <div className="remove-bg-toggle-group">
          <label className="compress-toggle">
            <input
              type="checkbox"
              checked={removeBackgroundEnabled}
              onChange={(e) => {
                const enabled = e.target.checked;
                setRemoveBackgroundEnabled(enabled);
                if (sources.length > 0 && !(stitchMode && stitchType === "animation") && sourceFiles.length > 0) {
                  clearResults();
                  setProcessing(new Set());
                  clearAnimationResults();
                  setProcessingAnimations(new Set());
                  handleFiles(createFileList(sourceFiles), compress, enabled, "replace");
                }
              }}
            />
            <span className="checkmark" />
            <span className="compress-label">Remove background</span>
          </label>
          <button
            type="button"
            className="remove-bg-help-btn"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setShowRemoveBgHelp(true);
            }}
            title="About background removal"
          >
            <HelpCircle size={14} />
          </button>
        </div>

        <div className={`stitch-toggle-group ${sources.length < 2 ? "disabled" : ""}`}>
          <label className={`compress-toggle ${sources.length < 2 ? "disabled" : ""}`}>
            <input
              type="checkbox"
              checked={stitchMode}
              disabled={sources.length < 2}
              onChange={async (e) => {
                const enabled = e.target.checked;
                setStitchMode(enabled);
                stitchModeRef.current = enabled;
                const jobId = bumpJobId();
                if (enabled && sources.length >= 2) {
                  clearResults();
                  setProcessing(new Set());
                  clearAnimationResults();
                  setProcessingAnimations(new Set());

                  if (stitchType === "grid") {
                    const optimal = getOptimalGrid(sources.length);
                    setGridConfig((prev) => ({ ...prev, ...optimal }));
                    const stitched = await stitchImages(sources, { ...gridConfig, ...optimal });
                    if (stitched) {
                      processStitchedImage(stitched, compress, undefined, jobId);
                    }
                  } else {
                    createAnimations(sources, frameDelay, compress, jobId, crossfade);
                  }
                } else if (!enabled) {
                  clearResults();
                  setProcessing(new Set());
                  clearAnimationResults();
                  setProcessingAnimations(new Set());
                  if (sources.length > 0) {
                    if (removeBackgroundEnabled && sourceFiles.length > 0) {
                      handleFiles(createFileList(sourceFiles), compress, removeBackgroundEnabled, "replace");
                    } else {
                      processImages(sources, compress, undefined, jobId);
                    }
                  }
                }
              }}
            />
            <span className="checkmark" />
            <span className="compress-label">
              Stitch into{" "}
              <select
                className="stitch-type-select"
                value={stitchType}
                disabled={sources.length < 2}
                onChange={async (e) => {
                  e.stopPropagation();
                  const newType = e.target.value as "grid" | "animation";
                  setStitchType(newType);
                  stitchTypeRef.current = newType;
                  if (stitchMode && sources.length >= 2) {
                    const jobId = bumpJobId();
                    clearResults();
                    setProcessing(new Set());
                    clearAnimationResults();
                    setProcessingAnimations(new Set());

                    if (newType === "grid") {
                      const optimal = getOptimalGrid(sources.length);
                      setGridConfig((prev) => ({ ...prev, ...optimal }));
                      const stitched = await stitchImages(sources, { ...gridConfig, ...optimal });
                      if (stitched) {
                        processStitchedImage(stitched, compress, undefined, jobId);
                      }
                    } else {
                      createAnimations(sources, frameDelay, compress, jobId, crossfade);
                    }
                  }
                }}
                onClick={(e) => e.stopPropagation()}
              >
                <option value="grid">grid</option>
                <option value="animation">animation</option>
              </select>
            </span>
          </label>
          <button
            type="button"
            className={`stitch-config-btn ${!stitchMode || sources.length < 2 ? "disabled" : ""}`}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              if (stitchMode && sources.length >= 2) {
                if (stitchType === "grid") {
                  setShowGridModal(true);
                } else {
                  setShowAnimationModal(true);
                }
              }
            }}
            title={stitchType === "grid" ? "Grid settings" : "Animation settings"}
            disabled={!stitchMode || sources.length < 2}
          >
            <Settings size={14} />
          </button>
        </div>

        <button
          type="button"
          className={`advanced-settings-toggle ${showAdvancedSettings ? "active" : ""}`}
          onClick={() => setShowAdvancedSettings((value) => !value)}
          aria-expanded={showAdvancedSettings}
          aria-controls="advanced-quality-settings"
        >
          <Settings size={14} />
          <span>Advanced</span>
        </button>
      </div>

      {showAdvancedSettings && (
        <div id="advanced-quality-settings" className="advanced-quality-panel">
          <div className="quality-row">
            <label htmlFor="jpeg-quality">JPEG quality</label>
            <input
              id="jpeg-quality"
              type="range"
              min="40"
              max="100"
              value={qualitySettings.jpegQuality}
              onChange={(event) => updateQualitySetting("jpegQuality", Number(event.target.value))}
            />
            <span>{qualitySettings.jpegQuality}</span>
          </div>
          <div className="quality-row">
            <label htmlFor="webp-quality">WebP quality</label>
            <input
              id="webp-quality"
              type="range"
              min="40"
              max="100"
              value={qualitySettings.webpQuality}
              onChange={(event) => updateQualitySetting("webpQuality", Number(event.target.value))}
            />
            <span>{qualitySettings.webpQuality}</span>
          </div>
          <div className="quality-row">
            <label htmlFor="avif-quality">AVIF quality</label>
            <input
              id="avif-quality"
              type="range"
              min="30"
              max="90"
              value={qualitySettings.avifQuality}
              onChange={(event) => updateQualitySetting("avifQuality", Number(event.target.value))}
            />
            <span>{qualitySettings.avifQuality}</span>
          </div>
          <div className="quality-row">
            <label htmlFor="avif-speed">AVIF speed</label>
            <input
              id="avif-speed"
              type="range"
              min="4"
              max="10"
              value={qualitySettings.avifSpeed}
              onChange={(event) => updateQualitySetting("avifSpeed", Number(event.target.value))}
            />
            <span>{qualitySettings.avifSpeed}</span>
          </div>
        </div>
      )}

      {/* Animation result display */}
      {stitchMode && stitchType === "animation" && sources.length >= 2 && (
        <div className="animation-result-section">
          <div className="animation-grid">
            {animationFormats.map((format) => {
              const result = animationResults[format.key];
              const isProcessing = processingAnimations.has(format.key);
              const hasResults = Boolean(result);
              const isCanceled = cancelledAnimationFormats.has(format.key);
              const statusMessage = isCanceled ? "Canceled" : animationErrors[format.key];

              return (
                <div
                  key={format.key}
                  role="button"
                  tabIndex={hasResults ? 0 : -1}
                  aria-disabled={!hasResults}
                  className={`format-card animation-card ${hasResults ? "has-results" : ""} ${
                    isProcessing ? "processing" : ""
                  } ${!hasResults ? "disabled" : ""}`}
                  onClick={() => result && saveAs(result.url, result.name)}
                  onKeyDown={(event) =>
                    handleFormatCardKeyDown(event, hasResults, () => {
                      if (result) saveAs(result.url, result.name);
                    })
                  }
                >
                  <div className="format-header">
                    <span className="format-name">{format.name}</span>
                    {isProcessing && (
                      <div className="format-header-actions">
                        <div className="spinner-small" />
                        <button
                          type="button"
                          className="format-cancel-btn"
                          onClick={(event) => {
                            event.stopPropagation();
                            cancelAnimationFormat(format.key);
                          }}
                          aria-label={`Cancel ${format.name} animation`}
                        >
                          cancel
                        </button>
                      </div>
                    )}
                  </div>

                  {hasResults && !isProcessing && <div className="download-hint">click to download</div>}
                  {statusMessage && (
                    <div className={`format-status-message ${isCanceled ? "neutral" : "error"}`}>
                      {statusMessage}
                    </div>
                  )}

                  <div className="animation-preview">
                    {hasResults ? (
                      format.preview === "video" ? (
                        <video src={result?.url} autoPlay loop muted playsInline />
                      ) : (
                        <img src={result?.url} alt={`${format.name} preview`} />
                      )
                    ) : (
                      <div className={`animation-placeholder ${isProcessing ? "pulsing" : ""}`} />
                    )}
                  </div>
                  <div className="format-footer">
                    <span className="file-count">1 image</span>
                    {hasResults && (
                      <span className="total-size">{formatFileSize(result.size)}</span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Regular format results - hidden when in animation mode */}
      {!(stitchMode && stitchType === "animation") && (isMobile ? (
        <div className="mobile-results">
          {availableFormats.map((format) => {
            const formatResults = results[format.mime] || [];
            const isProcessing = processing.has(format.mime);
            const hasResults = formatResults.length > 0;
            const progress = formatProgress[format.mime];
            const progressPercent = getProgressPercent(progress);
            const statusMessage = getFormatStatusMessage(progress, formatErrors[format.mime]);
            const progressLabel = progress && isProcessing
              ? `${Math.min(progress.completed, progress.total)}/${progress.total}`
              : null;

            return (
              <div key={format.mime} className="mobile-format-section">
                <div className="mobile-format-header">
                  <span className="format-name">{format.name}</span>
                  {(progressLabel || isProcessing) && (
                    <div className="format-header-actions">
                      {progressLabel && <span className="format-progress-count">{progressLabel}</span>}
                      {isProcessing && <div className="spinner-small" />}
                      {isProcessing && (
                      <button
                        type="button"
                        className="format-cancel-btn"
                        onClick={() => cancelFormat(format.mime)}
                        aria-label={`Cancel ${format.name} conversion`}
                      >
                        cancel
                      </button>
                      )}
                    </div>
                  )}
                  {hasResults && (
                    <span className="mobile-size">
                      {totalOriginalSize(format.mime) !== totalSize(format.mime)
                        ? <>{formatFileSize(totalOriginalSize(format.mime))} → <span className="compressed-size">{formatFileSize(totalSize(format.mime))}</span></>
                        : formatFileSize(totalSize(format.mime))}
                    </span>
                  )}
                </div>
                {isProcessing && progress && (
                  <div
                    className="format-progress-bar"
                    role="progressbar"
                    aria-valuemin={0}
                    aria-valuemax={progress.total}
                    aria-valuenow={Math.min(progress.completed, progress.total)}
                  >
                    <span style={{ width: `${progressPercent}%` }} />
                  </div>
                )}
                {statusMessage && (
                  <div className={`format-status-message ${progress?.canceled ? "neutral" : "error"}`}>
                    {statusMessage}
                  </div>
                )}
                {hasResults ? (
                  isPreviewable(format.mime) ? (
                    <>
                      <div className="mobile-images-grid">
                        {formatResults.map((r, i) => (
                          <img key={i} src={r.url} alt={r.name} className="mobile-result-img" />
                        ))}
                      </div>
                      <button
                        className="mobile-download-btn"
                        onClick={() => downloadAll(format.mime)}
                      >
                        Download {format.name} ({formatResults.length})
                      </button>
                    </>
                  ) : (
                    <button
                      className="mobile-download-btn"
                      onClick={() => downloadAll(format.mime)}
                    >
                      Download {format.name} ({formatResults.length})
                    </button>
                  )
                ) : (
                  <div className="mobile-placeholder-row">
                    {[0, 1, 2].map((i) => (
                      <div key={i} className={`mobile-placeholder ${isProcessing ? "pulsing" : ""}`} />
                    ))}
                  </div>
                )}
              </div>
            );
          })}
          {Object.keys(results).length > 0 && (
            <p className="mobile-hint">Tap Download to save files</p>
          )}
        </div>
      ) : (
        <div className="results-grid">
          {availableFormats.map((format) => {
            const formatResults = results[format.mime] || [];
            const isProcessing = processing.has(format.mime);
            const hasResults = formatResults.length > 0;
            const progress = formatProgress[format.mime];
            const progressPercent = getProgressPercent(progress);
            const statusMessage = getFormatStatusMessage(progress, formatErrors[format.mime]);
            const progressLabel = progress && isProcessing
              ? `${Math.min(progress.completed, progress.total)}/${progress.total}`
              : null;

            return (
              <div
                key={format.mime}
                role="button"
                tabIndex={hasResults ? 0 : -1}
                aria-disabled={!hasResults}
                className={`format-card ${hasResults ? "has-results" : ""} ${
                  isProcessing ? "processing" : ""
                } ${!hasResults ? "disabled" : ""}`}
                onClick={() => hasResults && downloadAll(format.mime)}
                onKeyDown={(event) =>
                  handleFormatCardKeyDown(event, hasResults, () => downloadAll(format.mime))
                }
              >
                <div className="format-header">
                  <span className="format-name">{format.name}</span>
                  {(progressLabel || isProcessing) && (
                    <div className="format-header-actions">
                      {progressLabel && <span className="format-progress-count">{progressLabel}</span>}
                      {isProcessing && <div className="spinner-small" />}
                      {isProcessing && (
                      <button
                        type="button"
                        className="format-cancel-btn"
                        onClick={(event) => {
                          event.stopPropagation();
                          cancelFormat(format.mime);
                        }}
                        aria-label={`Cancel ${format.name} conversion`}
                      >
                        cancel
                      </button>
                      )}
                    </div>
                  )}
                </div>

                {hasResults && !isProcessing && (
                  <div className="download-hint">click to download</div>
                )}
                {isProcessing && progress && (
                  <div
                    className="format-progress-bar"
                    role="progressbar"
                    aria-valuemin={0}
                    aria-valuemax={progress.total}
                    aria-valuenow={Math.min(progress.completed, progress.total)}
                  >
                    <span style={{ width: `${progressPercent}%` }} />
                  </div>
                )}
                {statusMessage && (
                  <div className={`format-status-message ${progress?.canceled ? "neutral" : "error"}`}>
                    {statusMessage}
                  </div>
                )}

                <div className="format-preview">
                  {hasResults ? (
                    <>
                      {formatResults.slice(0, 3).map((r, i) => (
                        <div key={i} className="preview-thumb">
                          <img src={isPreviewable(format.mime) ? r.url : (r.previewUrl || r.url)} alt="" />
                        </div>
                      ))}
                      {formatResults.length > 3 && (
                        <div className="preview-thumb more">
                          +{formatResults.length - 3}
                        </div>
                      )}
                    </>
                  ) : (
                    <>
                      {[0, 1, 2].map((i) => (
                        <div key={i} className={`preview-thumb placeholder ${isProcessing ? "pulsing" : ""}`} />
                      ))}
                    </>
                  )}
                </div>

                <div className="format-footer">
                  <span className="file-count">
                    {formatResults.length} file{formatResults.length !== 1 ? "s" : ""}
                  </span>
                  <span className="total-size">
                    {totalOriginalSize(format.mime) !== totalSize(format.mime)
                      ? <>{formatFileSize(totalOriginalSize(format.mime))} → <span className="compressed-size">{formatFileSize(totalSize(format.mime))}</span></>
                      : formatFileSize(totalSize(format.mime))}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      ))}

        <p className="footer-text">
          All transformations are 100% local in your browser. Images never leave
          your device.
        </p>
      </main>
    </div>
  );
};

export default App;
